import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { convertArrayBuffer } from "helpers/imageHelper";
import MessengersPopup from "components/common/MessengersPopup";
import MessengerLink, { MESSAGE_ICON_SIZE } from "components/common/MessengerLink";
import { getVisibleMessengers } from "helpers/messengers";
import VerifiedBadge from "components/common/VerifiedBadge";
import CopyIcon from '../../../img/copy.svg?react';

import styles from "./styles.module.scss";

import cn from "classnames";

const starIconSrc = new URL('../../../img/star-full.svg', import.meta.url).href;

const ContactsTableData = ({ content = {}, id, url }) => {
    const { t } = useTranslation();

    const user = content.user || {};
    // company может быть на уровне content или внутри user
    const company = content.company || user.company || {};

    // Название компании
    const companyName = content.company_name || company.name || "";

    // Верификация: у объявления флаг лежит либо на компании, либо на самом объявлении
    const isVerified = Boolean(company.is_verified || content.is_verified);

    // Рейтинг и отзывы
    const ratings = content.ratings;
    const rate = ratings?.rate || 0;
    const reviewsCount = ratings?.total;

    // Статусы оплаты
    const paymentsSummary = content.paymentsSummary;

    // Логотип/фото - приоритет: company.logo, photo, company_logo_url_small, company_logo_url
    const logoData = company.logo?.data || content.photo?.data || content.company_logo_url_small?.data || content.company_logo_url?.data;
    const companyLogo = logoData ? convertArrayBuffer(logoData) : null;

    // Данные контакта
    const fullName = [user.lastname, user.firstname].filter(Boolean).join(" ") || "";
    const email = user.email || "";
    // Телефон: user.phone (новый формат), user.mobile_phones (массив), user.mobile_phone (строка), company.phone
    const rawPhone = user.phone || (Array.isArray(user.mobile_phones) ? user.mobile_phones[0] : null) || user.mobile_phone || company.phone || "";
    // Убираем + в начале, если есть, чтобы не было ++
    const phone = rawPhone.replace(/^\+/, '');

    // Копирование телефона
    const [copied, setCopied] = useState(false);
    const handleCopyPhone = useCallback((e) => {
        e.stopPropagation();
        e.preventDefault();
        navigator.clipboard.writeText(`+${phone}`).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        });
    }, [phone]);

    // Мессенджеры без телефонных записей и неизвестных типов — иначе в строке остаётся пустой отступ
    const messengers = getVisibleMessengers(user.messengers);

    // Данные для MessengersPopup
    const employeeData = {
        firstname: user.firstname,
        lastname: user.lastname,
        middlename: user.middlename,
        mobile_phone: phone,
        email: email,
        messengers: user.messengers || [],
        company: { name: companyName, phone: company.phone, email: company.email },
    };

    const avatarContent = (
        <>
            {!companyLogo && (
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10 10C12.7614 10 15 7.76142 15 5C15 2.23858 12.7614 0 10 0C7.23858 0 5 2.23858 5 5C5 7.76142 7.23858 10 10 10Z" fill="#9D9D9D"/>
                    <path d="M10 12C5.58172 12 2 14.6863 2 18C2 19.1046 2.89543 20 4 20H16C17.1046 20 18 19.1046 18 18C18 14.6863 14.4183 12 10 12Z" fill="#9D9D9D"/>
                </svg>
            )}
        </>
    );

    return (
        <div className={styles.contacts}>
            {/* Desktop: header with logo + company name */}
            <div className={styles.contacts__header}>
                <MessengersPopup employee={employeeData} popupPosition="left" apiRoute={url} contactId={id}>
                    <div
                        className={styles.contacts__avatar}
                        style={companyLogo ? { backgroundImage: `url(${companyLogo})` } : undefined}
                    >
                        {avatarContent}
                    </div>
                </MessengersPopup>
                {companyName && (
                    <div className={styles.contacts__companyRow}>
                        <div className={styles.contacts__company} title={companyName}>{companyName}</div>
                        <VerifiedBadge isVerified={isVerified} size="small" />
                    </div>
                )}
            </div>
            <div className={styles.contacts__info}>
                {companyName && (
                    <div className={styles.contacts__field}>
                        <span className={styles.contacts__subLabel}>{t('contacts.companyName')}</span>
                        <div className={styles.contacts__companyRowMobile}>
                            <div className={styles.contacts__companyMobile} title={companyName}>{companyName}</div>
                            <VerifiedBadge isVerified={isVerified} size="small" />
                        </div>
                    </div>
                )}

                {/* Рейтинг, отзывы */}
                {(rate > 0 || reviewsCount > 0 || (paymentsSummary && (!!paymentsSummary.paidInTimeCount || !!paymentsSummary.paidWithDelayCount || !!paymentsSummary.notPaidCount))) && (
                    <div className={styles.contacts__field}>
                        <span className={styles.contacts__subLabel}>{t('contacts.ratingAndReviews')}</span>
                        <div className={styles.contacts__ratingRow}>
                            <div className={styles.contacts__ratingGroup}>
                                {rate > 0 && (
                                    <span className={styles.contacts__rating}>
                                        <img src={starIconSrc} alt={t('rating')} className={styles.contacts__starIcon} />
                                        <span className={styles.contacts__rateValue}>{rate.toFixed(1)}</span>
                                    </span>
                                )}
                                {reviewsCount > 0 && (
                                    <span className={styles.contacts__reviewsCount}>
                                        ({reviewsCount})
                                    </span>
                                )}
                            </div>
                            {paymentsSummary && (!!paymentsSummary.paidInTimeCount || !!paymentsSummary.paidWithDelayCount || !!paymentsSummary.notPaidCount) && (
                                <div className={styles.contacts__paymentStatus}>
                                    <span className={styles.contacts__paymentGreen}>{paymentsSummary.paidInTimeCount ?? 0}</span>
                                    <span className={styles.contacts__paymentSeparator}>/</span>
                                    <span className={styles.contacts__paymentYellow}>{paymentsSummary.paidWithDelayCount ?? 0}</span>
                                    <span className={styles.contacts__paymentSeparator}>/</span>
                                    <span className={styles.contacts__paymentRed}>{paymentsSummary.notPaidCount ?? 0}</span>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {fullName && (
                    <div className={styles.contacts__field}>
                        <span className={styles.contacts__subLabel}>{t('contacts.employee')}</span>
                        <div className={styles.contacts__name} title={fullName}>{fullName}</div>
                    </div>
                )}
                {email && (
                    <div className={styles.contacts__field}>
                        <span className={styles.contacts__subLabel}>{t('contacts.emailLabel')}</span>
                        <a
                            href={`mailto:${email}`}
                            className={styles.contacts__link}
                            onClick={(e) => e.stopPropagation()}
                            title={email}
                        >
                            {email}
                        </a>
                    </div>
                )}
                {phone && (
                    <div className={styles.contacts__field}>
                        <span className={styles.contacts__subLabel}>{t('contacts.phoneLabel')}</span>
                        <div className={styles.contacts__phoneRow}>
                            <a
                                href={`tel:+${phone}`}
                                className={`${styles.contacts__link} ${styles['contacts__link--phone']}`}
                                onClick={(e) => e.stopPropagation()}
                                title={`+${phone}`}
                            >
                                +{phone}
                            </a>
                            <button
                                type="button"
                                className={cn(
                                    styles.contacts__copyButton,
                                    copied ? styles.contacts__copyButtonActive : styles.contacts__copyButtonInactive,
                                )}
                                onClick={handleCopyPhone}
                            >
                                <CopyIcon className={styles.contacts__copyIcon} />
                            </button>
                        </div>
                    </div>
                )}
                {/* TODO: Временное решение - иконки мессенджеров под телефоном */}
                {messengers.length > 0 && (
                    <div className={styles.contacts__messengers} onClick={(e) => e.stopPropagation()}>
                        {messengers.map((item, idx) => (
                            <MessengerLink
                                key={idx}
                                item={item}
                                size={MESSAGE_ICON_SIZE.compact}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ContactsTableData;
